import { TrackerEvent } from './tracker-event';
import { idToString, convertMonetaryValueToNumber, toInt } from './util';
import { loadScript } from '@fastcampus/browser-util';

/**
 * Facebook Pixel
 *
 * @see www/view/_partials/_tracker_facebook.html
 * @see https://developers.facebook.com/docs/facebook-pixel/
 */
export class FacebookTracker {
  static create(opts, context) {
    return new FacebookTracker(opts, context);
  }

  constructor({ id, userId }, context) {
    this.name = 'pixel';
    this.id = id;
    this.userId = userId;

    const fbq = function () {
      fbq.callMethod ? fbq.callMethod.apply(fbq, arguments) : fbq.queue.push(arguments);
    };
    context.fbq = fbq;
    if (!context._fbq) context._fbq = fbq;
    fbq.push = fbq;
    fbq.loaded = !0;
    fbq.version = '2.0';
    fbq.queue = [];
    loadScript('https://connect.facebook.net/en_US/fbevents.js')
      .then(() => {})
      .catch((err) => console.error('install err!', err));

    fbq('init', this.id);
    if (this.userId) {
      fbq('setUserProperties', this.id, {
        user_id: parseInt(this.userId) || 'none',
        $gender: this.gender || 'none',
        $user_type: this.userType || 'normal',
        user_class: this.grade || 'normal',
      });
    }
    fbq('track', 'PageView');

    // 스크립트 로딩 전에도 이벤트 큐잉! -> connect!
    this.fbq = fbq;
    this.ready = true;
  }

  getPayloadForSPA(data) {
    const { product, bundle, courses } = data;
    const payload = {
      content_category: idToString(product.formatId) + '/' + idToString(product.categoryId),
      content_name: product.title,
      content_type: 'product',
      value: product.salePrice || 0,
      currency: 'KRW',
      contents: [
        {
          id: product.id,
          content_category: idToString(product.formatId) + '/' + idToString(product.categoryId),
          quantity: product.quantity || 1,
          item_price: product.salePrice || 0,
          course_id: product.courseId || 0,
        },
      ],
      content_ids: [product.id],
    };
    if (bundle) {
      payload.content_ids = payload.content_ids.concat(bundle.map((product) => product.id));
      const contents = bundle.map((product) => {
        return {
          id: product.id,
          content_category: idToString(product.formatId) + '/' + idToString(product.categoryId),
          quantity: product.quantity || 1,
          item_price: product.salePrice || 0,
          course_id: product.courseId || 0,
        };
      });
      payload.contents = payload.contents.concat(contents);
    }
    if (courses) {
      payload.course_ids = courses.map((course) => course.id);
      payload.origin_course_ids = courses
        .filter((course) => course.originCourseId || course.id)
        .map((course) => course.originCourseId || course.id);
      payload.contents = payload.contents.map((product) => {
        const course = courses.find((course) => course.id === product.course_id);
        product.origin_course_id = (course && course.originCourseId) || 0;
        return product;
      });
    }
    return payload;
  }

  getPayloadForCourseView(course) {
    const product = course.products && course.products[0];
    const payload = {
      content_ids: product ? [toInt(product.id)] : [],
      content_category: idToString(course.formatId) + '/' + idToString(course.categoryId),
      content_name: course.title,
      content_type: 'product',
      contents: [],
      origin_course_ids: course.originCourseId ? [toInt(course.originCourseId)] : [toInt(course.id)],
      course_ids: [toInt(course.id)],
    };
    if (product) {
      payload.content = [
        {
          id: toInt(product.id),
          content_category: idToString(course.formatId) + '/' + idToString(course.categoryId),
          quantity: toInt(product.quantity) || 1,
          item_price: convertMonetaryValueToNumber(product.salePrice),
          origin_course_id: toInt(course.originCourseId || course.id),
          course_id: toInt(course.id),
        },
      ];
    }
    return payload;
  }

  getPayloadForCategoryView(category) {
    const payload = {
      user_id: this.userId,
      content_category: idToString(category.formatId) + '/' + idToString(category.categoryId),
      content_name: category.categoryId,
      content_type: 'product',
    };
    if (category.courses) {
      payload.contents = category.courses
        .filter((course) => course.product)
        .map((course) => {
          return {
            id: toInt(course.product.id),
            content_category: idToString(course.formatId) + '/' + idToString(course.categoryId),
            quantity: toInt(course.product.quantity) || 1,
            item_price: convertMonetaryValueToNumber(course.product.salePrice),
            origin_course_id: toInt(course.originCourseId || course.id),
            course_id: toInt(course.id),
          };
        });
      payload.content_ids = category.courses
        .filter((course) => course.product)
        .map((course) => toInt(course.product.id));
      payload.course_ids = category.courses.map((course) => toInt(course.id));
      payload.origin_course_ids = category.courses.map((course) => toInt(course.originCourseId || course.id));
    }
    return payload;
  }

  getPayloadForTransactionPayment(data) {
    const { productSaleInfo, transactionInfo } = data;
    const payload = {
      content_type: 'product',
      currency: 'KRW',
      contents: [],
      content_ids: [],
    };

    if (transactionInfo && transactionInfo.transaction) {
      const { amount, taxFreeAmount, id } = transactionInfo.transaction;
      payload.value = amount;
      payload.tax = amount - taxFreeAmount;
      payload.tax_free_amount = taxFreeAmount;
      payload.transaction_id = id;
    }

    if (transactionInfo && transactionInfo.coupons && Array.isArray(transactionInfo.coupons)) {
      payload.coupon_ids = transactionInfo.coupons.map((coupon) => coupon.id);
    }

    if (productSaleInfo && productSaleInfo.product) {
      const { formatId, categoryId, title, id, quantity, salePrice, courseId } = productSaleInfo.product;
      payload.content_category = idToString(formatId) + '/' + idToString(categoryId);
      payload.content_name = title;
      payload.contents = payload.contents.concat({
        id,
        content_category: idToString(formatId) + '/' + idToString(categoryId),
        quantity: quantity || 1,
        item_price: salePrice,
        course_id: courseId || '',
      });
      payload.content_ids.push(id);
    }

    if (productSaleInfo && productSaleInfo.bundle) {
      const bundle = productSaleInfo.bundle.map((product) => {
        return {
          id: product.id,
          content_category: idToString(product.formatId) + '/' + idToString(product.categoryId),
          quantity: product.quantity || 1,
          item_price: product.salePrice,
          course_id: product.courseId || '',
        };
      });
      payload.contents = payload.contents.concat(bundle);
      payload.content_ids = payload.content_ids.concat(bundle.map((product) => product.id));
    }

    if (productSaleInfo && productSaleInfo.courses) {
      const courses = productSaleInfo.courses;
      payload.course_ids = courses.map((course) => course.id);
      payload.origin_course_ids = courses.map((course) => course.originCourseId || course.id);
      payload.contents = payload.contents.map((content) => {
        const course = courses.find((course) => course.id === content.course_id);
        content.origin_course_id = (course && course.originCourseId) || 0;
        return content;
      });
    }

    return payload;
  }

  track(event, data) {
    if (!this.ready) {
      if (process.env['NODE_ENV'] !== 'prod') console.error('**ignore** not ready!', event, data);
    }
    switch (event) {
      case TrackerEvent.PAGE_VIEW:
        break;
      case TrackerEvent.USER_SIGNUP:
        this.fbq('trackCustom', 'Signup');

        break;
      case TrackerEvent.USER_LOGIN:
        this.fbq('trackCustom', 'EnterLogin');

        break;
      case TrackerEvent.FORMAT_VIEW:
        break;
      case TrackerEvent.CATEGORY_VIEW:
        if (data) {
          const payload = this.getPayloadForCategoryView(data);
          this.fbq('track', 'ViewContent', payload);
        }
        break;
      case TrackerEvent.COURSE_VIEW:
        if (data) {
          const payload = this.getPayloadForCourseView(data);
          this.fbq('track', 'ViewContent', payload);
        }
        break;
      case TrackerEvent.ENROLLMENT_VIEW:
        if (data) {
          const payload = this.getPayloadForSPA(data);
          this.fbq('trackCustom', 'InitiateEnrollment', payload);
        }
        break;
      case TrackerEvent.ENROLLMENT_PENDING:
        if (data) {
          const payload = this.getPayloadForSPA(data);
          this.fbq('track', 'Lead', payload);
        }
        break;
      case TrackerEvent.TRANSACTION_ORDER:
        if (data) {
          const payload = this.getPayloadForSPA(data);
          this.fbq('track', 'InitiateCheckout', payload);
        }
        break;
      case TrackerEvent.TRANSACTION_PAYMENT:
        if (data) {
          const payload = this.getPayloadForTransactionPayment(data);
          this.fbq('track', 'Purchase', payload);
        }
        break;
      case TrackerEvent.COMMON: {
        break;
      }
    }
  }
}
